import { mdiCheck, mdiClose } from '@mdi/js';
import { SxProps } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { Chip, IChipProps } from 'common/components/Chip';
import { CircularProgress } from 'common/components/CircularProgress';
import { Icon } from 'common/components/Icon';
import {
    IPortfolioItemListItem,
    IPortfolioItemScoreTypeChoices,
} from 'graphql/types';
import { receivedCertificateEndDateNear } from 'common/utils/receivedCertificate';
import { withSxProp } from 'common/utils/props';

type TSkillPortfolioItem = Pick<
    IPortfolioItemListItem,
    | 'completionDate'
    | 'endDate'
    | 'progress'
    | 'score'
    | 'scoreThreshold'
    | 'scoreType'
> &
    Partial<
        Pick<
            IPortfolioItemListItem,
            | 'isCompliant'
            | 'scoreMax'
            | 'relatedObjectHasComments'
            | 'relatedObjectHasFiles'
        >
    >;

interface IProps {
    portfolioItem: TSkillPortfolioItem;
    hover?: boolean;
    size?: 'small' | 'normal' | 'large';
    sx?: SxProps;
    withIcon?: boolean;
    onClick?(): void;
}

export const SkillProgressChip = ({
    portfolioItem,
    hover,
    size = 'normal',
    sx,
    withIcon,
    onClick,
}: IProps) => {
    const [translate] = useTranslation();

    const {
        completionDate,
        endDate,
        isCompliant,
        progress,
        relatedObjectHasComments,
        relatedObjectHasFiles,
        score,
        scoreMax,
        scoreThreshold,
        scoreType,
    } = portfolioItem;

    const showDot = !!(relatedObjectHasComments || relatedObjectHasFiles);

    let sizeStyle: SxProps = {};
    let circularProgressChipSize = 28;

    if (size === 'small') {
        sizeStyle = {
            width: 23,
            height: 23,
            minWidth: 0,
        };
    } else if (size === 'large') {
        sizeStyle = {
            width: 40,
            height: 40,
            minWidth: 0,
            borderRadius: '50%',
        };

        circularProgressChipSize = 40;
    }

    if (withIcon) {
        sizeStyle = {
            ...sizeStyle,
            '.MuiChip-icon': {
                mx: 'auto',
                color: 'common.white',
                fontSize: size === 'large' ? 22 : 16,
            },
            '.MuiChip-label': { display: 'none' },
        };
    }

    const ariaLabel = !!onClick || hover ? translate('progress') : undefined;

    // Compliancy is a skill concept and takes precedence over progress/score.
    if (!!isCompliant) {
        return (
            <Chip
                aria-label={`${translate('score')} ${translate('fulfilled')}`}
                color="success"
                hover={hover}
                icon={
                    withIcon ? <Icon path={mdiCheck} size="2rem" /> : undefined
                }
                showDot={showDot}
                sx={[...withSxProp(sx), sizeStyle]}
                tabIndex={!!onClick ? 0 : -1}
                onClick={onClick}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') onClick?.();
                }}
            />
        );
    }

    if (isCompliant === false) {
        return (
            <Chip
                aria-label={`${translate('score')} ${translate('notFulfilled')}`}
                color="error"
                hover={hover}
                icon={
                    withIcon ? <Icon path={mdiClose} size="2rem" /> : undefined
                }
                showDot={showDot}
                sx={[...withSxProp(sx), sizeStyle]}
                tabIndex={!!onClick ? 0 : -1}
                onClick={onClick}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') onClick?.();
                }}
            />
        );
    }

    // Active progress on a (re-)certification attempt takes precedence over
    // an expired endDate: surface the in-progress chip rather than the grey
    // expired fallback. Matches useSkillStatusDescription.
    if (progress && progress < 1 && !completionDate) {
        const progressValue = Math.round(progress * 100);

        return (
            <CircularProgress
                filled
                showTooltip
                aria-label={`${ariaLabel} ${progressValue}%`}
                bgColor="grey.200"
                color="inherit"
                hover={hover}
                showDot={showDot}
                size={circularProgressChipSize}
                sx={sx}
                tabIndex={!!onClick ? 0 : -1}
                value={progressValue}
                variant="determinate"
                onClick={onClick}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') onClick?.();
                }}
            />
        );
    }

    // Expired takes precedence over remaining progress/score: a nullish
    // isCompliant means no mandatory certificates remain, so fall back to
    // a neutral grey chip rather than reporting the old score.
    if (endDate && new Date(endDate) < new Date()) {
        return (
            <Chip
                aria-label={ariaLabel}
                hover={hover}
                showDot={showDot}
                sx={[
                    ...withSxProp(sx),
                    { backgroundColor: 'grey.200', ...sizeStyle },
                ]}
                tabIndex={!!onClick ? 0 : -1}
                onClick={onClick}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') onClick?.();
                }}
            />
        );
    }

    // Near expiry: warning-tinted progress while in-progress, warning chip
    // once fully completed.
    if (endDate && receivedCertificateEndDateNear(endDate)) {
        if (progress !== 1) {
            const progressValue = Math.round((progress || 0) * 100);

            return (
                <CircularProgress
                    filled
                    showTooltip
                    aria-label={`${ariaLabel} ${progressValue}%`}
                    bgColor="grey.200"
                    color="inherit"
                    hover={hover}
                    showDot={showDot}
                    size={circularProgressChipSize}
                    sx={sx}
                    tabIndex={!!onClick ? 0 : -1}
                    value={progressValue}
                    variant="determinate"
                    onClick={onClick}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') onClick?.();
                    }}
                />
            );
        }

        return (
            <Chip
                aria-label={ariaLabel}
                color="warning"
                hover={hover}
                icon={
                    withIcon ? <Icon path={mdiCheck} size="2rem" /> : undefined
                }
                showDot={showDot}
                sx={[...withSxProp(sx), sizeStyle]}
                tabIndex={!!onClick ? 0 : -1}
                onClick={onClick}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') onClick?.();
                }}
            />
        );
    }

    // Still in progress: not yet 100% and no completion date recorded.
    if (progress !== 1 && !completionDate) {
        const progressValue = Math.round((progress || 0) * 100);

        return (
            <CircularProgress
                filled
                showTooltip
                aria-label={`${ariaLabel} ${progressValue}%`}
                bgColor="grey.200"
                color="inherit"
                hover={hover}
                showDot={showDot}
                size={circularProgressChipSize}
                sx={sx}
                tabIndex={!!onClick ? 0 : -1}
                value={progressValue}
                variant="determinate"
                onClick={onClick}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') onClick?.();
                }}
            />
        );
    }

    // Completed: score-based chip.
    let scoreValue: React.ReactNode;
    let status: string | undefined;
    const scoreChipProps: IChipProps = {};

    if (score !== undefined && score !== null) {
        const showScoreValue =
            score !== 0 &&
            scoreType !== null &&
            scoreType !== IPortfolioItemScoreTypeChoices.Fulfilled;

        scoreValue = showScoreValue ? `${Math.round(score * 100)}%` : undefined;

        let chipColor: IChipProps['color'];

        if (scoreThreshold) {
            const isSuccess = score >= scoreThreshold;

            chipColor = isSuccess ? 'success' : 'error';
            status = translate(isSuccess ? 'fulfilled' : 'notFulfilled');
        } else if (scoreMax === 0) {
            chipColor = 'success';
            status = translate('fulfilled');
        } else {
            chipColor = score ? 'success' : 'error';
            status = translate(score ? 'fulfilled' : 'notFulfilled');
        }

        scoreChipProps.color = chipColor;

        if (withIcon) {
            scoreChipProps.icon =
                chipColor === 'error' ? (
                    <Icon path={mdiClose} size="2rem" />
                ) : (
                    <Icon path={mdiCheck} size="2rem" />
                );
        }
    } else {
        scoreChipProps.sx = {
            backgroundColor: !scoreType ? 'grey.600' : 'grey.200',
        };
    }

    return (
        <Chip
            aria-label={`${translate('score')} ${scoreValue || status}`}
            sx={[...withSxProp(sx), sizeStyle]}
            {...scoreChipProps}
            hover={hover}
            label={scoreValue}
            showDot={!scoreValue && showDot}
            tabIndex={!!onClick ? 0 : -1}
            onClick={onClick}
            onKeyDown={(e) => {
                if (e.key === 'Enter') onClick?.();
            }}
        />
    );
};
